import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import passwordImage from "../assets/password.jpg";
import { PASSWORD_POLICY } from "../hooks/usePasswordChange";

export default function PasswordChangeDialog({ flow, onSignIn }) {
  const { passwords, passwordError, saving, saved, expired, handlePasswordChange, handleSavePassword, handleClosePopup } = flow;
  const dialog = useRef(null);
  const wasSaving = useRef(false);
  const close = useRef(handleClosePopup);
  close.current = handleClosePopup;
  const [viewport, setViewport] = useState({});
  useEffect(() => {
    const previousFocus = document.activeElement;
    const updateViewport = () => {
      const visible = window.visualViewport;
      setViewport(visible ? { top: visible.offsetTop, height: visible.height } : {});
      requestAnimationFrame(() => {
        if (dialog.current?.contains(document.activeElement)) document.activeElement.scrollIntoView?.({ block: "nearest" });
      });
    };
    updateViewport();
    window.visualViewport?.addEventListener("resize", updateViewport);
    window.visualViewport?.addEventListener("scroll", updateViewport);
    dialog.current?.querySelector("input")?.focus();
    return () => {
      window.visualViewport?.removeEventListener("resize", updateViewport);
      window.visualViewport?.removeEventListener("scroll", updateViewport);
      previousFocus?.focus?.();
    };
  }, []);
  useEffect(() => {
    if (saved || expired) dialog.current?.querySelector("button")?.focus();
  }, [saved, expired]);
  useEffect(() => {
    if (saving) {
      wasSaving.current = true;
      dialog.current?.focus({ preventScroll: true });
      return;
    }
    if (wasSaving.current) {
      wasSaving.current = false;
      if (!saved && !expired) {
        dialog.current
          ?.querySelector("input:not(:disabled), button:not(:disabled)")
          ?.focus({ preventScroll: true });
      }
    }
  }, [saving, saved, expired]);
  const handleKeyDown = (event) => {
    if (event.key === "Escape" && !saved && !expired) { event.preventDefault(); close.current(); }
    if (event.key !== "Tab") return;
    const currentDialog = dialog.current;
    if (!currentDialog) return;
    const controls = [...currentDialog.querySelectorAll("input:not(:disabled), button:not(:disabled)")];
    if (controls.length === 0) {
      event.preventDefault();
      currentDialog.focus({ preventScroll: true });
      return;
    }
    const first = controls[0], last = controls[controls.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === currentDialog)) { event.preventDefault(); last?.focus(); }
    if (!event.shiftKey && (active === last || active === currentDialog)) { event.preventDefault(); first?.focus(); }
  };
  return createPortal(
    <div className="settings-page">
      <div className="settings-popup-overlay" style={viewport}>
        <div className="settings-popup" ref={dialog} role="dialog" aria-modal="true" aria-labelledby="password-dialog-title" aria-busy={saving} tabIndex={-1} onKeyDown={handleKeyDown}>
          <img src={passwordImage} alt="Password Security" className="settings-popup-hero-img" />
          <h2 id="password-dialog-title">Edit Password</h2>
          <form className="settings-popup-content" onSubmit={handleSavePassword} aria-busy={saving}>
            {saved ? <p role="status" className="settings-password-status success">Password updated. Sign in with your new password.</p> : <>
              <p id="password-policy" className="settings-password-status info">{PASSWORD_POLICY} Changing your password signs you out.</p>
              {[
                ["currentPassword", "Current Password", "current-password"],
                ["newPassword", "New Password", "new-password"],
                ["confirmPassword", "Confirm Password", "new-password"],
              ].map(([name, label, autocomplete]) => <React.Fragment key={name}>
                <label htmlFor={name}>{label}:</label>
                <input id={name} type="password" name={name} value={passwords[name]} onChange={handlePasswordChange} autoComplete={autocomplete} aria-describedby="password-policy" disabled={saving || expired} required />
              </React.Fragment>)}
              {!passwordError && passwords.newPassword && passwords.confirmPassword && (
                <p className={`settings-password-status ${passwords.newPassword === passwords.confirmPassword ? "success" : "error"}`}>
                  {passwords.newPassword === passwords.confirmPassword ? "Passwords match." : "Passwords do not match."}
                </p>
              )}
            </>}
            {passwordError && <p role="alert" className="settings-popup-error-message">{passwordError}</p>}
            <div className="settings-popup-form-buttons">
              {saved || expired ? <button type="button" onClick={onSignIn}>Sign in</button> : <button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</button>}
              {!saved && !expired && <button type="button" onClick={handleClosePopup} disabled={saving}>Cancel</button>}
            </div>
          </form>
        </div>
      </div>
    </div>, document.body
  );
}
